import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../services/apiClient";
import { ReGenerateToken } from "../auth/authSlice";
import { GetDecryptEmailformat, GetDecryptReferenceNumberformat } from "../../Components/Common/Common";

// Google Drive integration (scope drive.file). apiClient's baseURL is
// REACT_APP_apiBaseUrl and it attaches the Bearer token automatically.
//   /google-api/connection-status, /authorize-granular-scope, /revoke-granular-scope
//   /google-api/api/v1/drive/{upload,list,download,delete}
//
// Sandbox gate: while TAC review is pending, only accounts listed in
// GOOGLE_DRIVE_SANDBOX_EMAILS get `data.services.drive` in connection-status.
// When it is absent, the UI hides the Drive page and sidebar entry.
const GOOGLE_API_BASE = "/google-api";
const DRIVE_BASE = `${GOOGLE_API_BASE}/api/v1/drive`;
export const DRIVE_SERVICE = "drive";
const LEGACY_DRIVE_KEY = "google_drive";

const getIdentity = () => ({
  email: GetDecryptEmailformat() || localStorage?.getItem("email"),
  reference_number: GetDecryptReferenceNumberformat() || localStorage?.getItem("reference_number"),
});

const unwrapResponse = (response) => response?.data?.data ?? response?.data ?? response;

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong";

const isAuthError = (error) => error?.response?.status === 401;

// On an auth error, refresh the token via ReGenerateToken (same as plmHubSlice)
// and retry the thunk once. `_retried` stops an endless refresh loop.
const handleError = async (error, thunk, arg, { dispatch, rejectWithValue }) => {
  if (isAuthError(error) && !arg?._retried) {
    const action = await dispatch(ReGenerateToken(thunk, { ...(arg || {}), _retried: true }, dispatch));
    if (action?.meta?.requestStatus === "fulfilled") return action.payload;
    return rejectWithValue(action?.payload || getErrorMessage(error));
  }
  return rejectWithValue(getErrorMessage(error));
};

// 1. GET /google-api/connection-status?email=&reference_number=
export const fetchDriveConnectionStatus = createAsyncThunk(
  "googleDrive/connectionStatus",
  async (arg, thunkApi) => {
    try {
      const response = await apiClient.get(`${GOOGLE_API_BASE}/connection-status`, {
        params: getIdentity(),
      });
      return unwrapResponse(response);
    } catch (error) {
      // "Not connected" comes back as a 404 whose body still carries the
      // per-service flags; treat that as a valid (disconnected) status.
      const body = error?.response?.data;
      if (!isAuthError(error) && body?.data && typeof body.data === "object") {
        return body.data;
      }
      return handleError(error, fetchDriveConnectionStatus, arg, thunkApi);
    }
  }
);

// 2. POST /google-api/authorize-granular-scope
// Body: { email, reference_number, services: ["google_drive"], service: "drive" }
// Returns the drive.file consent URL (authorization_url / message).
export const authorizeDriveScope = createAsyncThunk(
  "googleDrive/authorize",
  async (arg, thunkApi) => {
    try {
      const response = await apiClient.post(`${GOOGLE_API_BASE}/authorize-granular-scope`, {
        ...getIdentity(),
        services: [LEGACY_DRIVE_KEY],
        service: DRIVE_SERVICE,
      });
      return unwrapResponse(response);
    } catch (error) {
      return handleError(error, authorizeDriveScope, arg, thunkApi);
    }
  }
);

// 7. POST /google-api/revoke-granular-scope - revokes only the Drive scope and
// returns the updated connection status. Any reauth_url for the remaining
// services is ignored; the UI never redirects on disconnect.
export const revokeDriveScope = createAsyncThunk(
  "googleDrive/revoke",
  async (arg, thunkApi) => {
    try {
      const response = await apiClient.post(`${GOOGLE_API_BASE}/revoke-granular-scope`, {
        ...getIdentity(),
        service: DRIVE_SERVICE,
        service_to_revoke: LEGACY_DRIVE_KEY,
      });
      return unwrapResponse(response);
    } catch (error) {
      return handleError(error, revokeDriveScope, arg, thunkApi);
    }
  }
);

// 3. POST /api/v1/drive/upload - multipart { file, reference_number }.
export const uploadDriveFile = createAsyncThunk(
  "googleDrive/upload",
  async (arg, thunkApi) => {
    const { file } = arg;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("reference_number", getIdentity().reference_number);
      const response = await apiClient.post(`${DRIVE_BASE}/upload`, formData, {
        // Drop the JSON default so the browser sets the multipart boundary.
        headers: { "Content-Type": undefined },
        timeout: 0,
      });
      return unwrapResponse(response);
    } catch (error) {
      return handleError(error, uploadDriveFile, arg, thunkApi);
    }
  }
);

// 4. GET /api/v1/drive/list?reference_number=&pageSize=&pageToken=
export const listDriveFiles = createAsyncThunk(
  "googleDrive/list",
  async (arg, thunkApi) => {
    const { pageSize = 10, pageToken = "" } = arg || {};
    try {
      const params = { reference_number: getIdentity().reference_number, pageSize };
      if (pageToken) params.pageToken = pageToken;
      const response = await apiClient.get(`${DRIVE_BASE}/list`, { params });
      return unwrapResponse(response);
    } catch (error) {
      return handleError(error, listDriveFiles, arg, thunkApi);
    }
  }
);

// 5. GET /api/v1/drive/download?reference_number=&fileId= - binary response.
// Resolves with { fileId, fileName, blob } so the component can trigger a save.
export const downloadDriveFile = createAsyncThunk(
  "googleDrive/download",
  async (arg, thunkApi) => {
    const { fileId, fileName } = arg;
    try {
      const response = await apiClient.get(`${DRIVE_BASE}/download`, {
        params: { reference_number: getIdentity().reference_number, fileId },
        responseType: "blob",
        timeout: 0,
      });
      const disposition = response?.headers?.["content-disposition"] || "";
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const resolvedName = fileName || (match ? decodeURIComponent(match[1]) : fileId);
      return { fileId, fileName: resolvedName, blob: response.data };
    } catch (error) {
      return handleError(error, downloadDriveFile, arg, thunkApi);
    }
  }
);

// 6. DELETE /api/v1/drive/delete?reference_number=&fileId=
export const deleteDriveFile = createAsyncThunk(
  "googleDrive/delete",
  async (arg, thunkApi) => {
    const { fileId } = arg;
    try {
      const response = await apiClient.delete(`${DRIVE_BASE}/delete`, {
        params: { reference_number: getIdentity().reference_number, fileId },
      });
      return { fileId, data: unwrapResponse(response) };
    } catch (error) {
      return handleError(error, deleteDriveFile, arg, thunkApi);
    }
  }
);

// Consent URL from the authorize response (`authorization_url`), with
// fallbacks for other field names some backends use.
export const getDriveAuthorizeUrl = (data) => {
  const candidate =
    data?.authorization_url || data?.authorizationUrl || data?.url || data?.auth_url || data?.message || null;
  return typeof candidate === "string" && /^https?:\/\//i.test(candidate) ? candidate : null;
};

const asFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (value && typeof value === "object") {
    if (typeof value.connected === "boolean") return value.connected;
    if (typeof value.is_active === "boolean") return value.is_active;
    if (typeof value.is_connected === "boolean") return value.is_connected;
  }
  return null;
};

// Normalise the (unwrapped) connection-status payload into
// { available, connected, email }.
//   available: false when `services.drive` is absent - the user is not in the
//              Drive sandbox, so the Drive UI must be hidden entirely.
// Preferred shape: { services: { drive: { connected } }, google_email }.
// Legacy shape:    { google_drive: { is_active } }.
export const parseDriveStatus = (data) => {
  const result = { available: false, connected: false, email: null };
  if (typeof data === "boolean") return { ...result, available: true, connected: data };
  if (!data || typeof data !== "object") return result;

  result.email = data.google_email || data.email || null;

  const services = data.services || data.connected_services || data.data;
  const entry =
    (services && typeof services === "object" && !Array.isArray(services)
      ? services[DRIVE_SERVICE] ?? services[LEGACY_DRIVE_KEY]
      : undefined) ??
    data[DRIVE_SERVICE] ??
    data[LEGACY_DRIVE_KEY];

  if (Array.isArray(services)) {
    const listed = services.includes(DRIVE_SERVICE) || services.includes(LEGACY_DRIVE_KEY);
    return { ...result, available: listed, connected: listed };
  }

  const flag = asFlag(entry);
  if (flag !== null) {
    result.available = true;
    result.connected = flag;
    if (entry && typeof entry === "object") result.email = entry.google_email || entry.email || result.email;
  }
  return result;
};

export const isDriveConnected = (data) => parseDriveStatus(data).connected;

const toFileList = (payload) =>
  Array.isArray(payload) ? payload : payload?.files || payload?.items || [];

const initialState = {
  // checked: true once the first status request has completed (so the
  // sidebar does not flash the Drive link before the sandbox gate is known).
  status: { data: null, checked: false, available: false, connected: false, email: null, loading: false, error: null },
  authorize: { data: null, loading: false, error: null },
  revoke: { data: null, loading: false, error: null },
  files: { data: [], nextPageToken: null, loading: false, error: null },
  upload: { data: null, loading: false, error: null },
  download: { fileId: null, loading: false, error: null },
  remove: { fileId: null, loading: false, error: null },
};

const googleDriveSlice = createSlice({
  name: "googleDrive",
  initialState,
  reducers: {
    resetGoogleDriveState: () => initialState,
    clearDriveFiles: (state) => {
      state.files = initialState.files;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDriveConnectionStatus.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchDriveConnectionStatus.fulfilled, (state, action) => {
        const parsed = parseDriveStatus(action.payload);
        state.status.loading = false;
        state.status.checked = true;
        state.status.data = action.payload;
        state.status.available = parsed.available;
        state.status.connected = parsed.connected;
        state.status.email = parsed.email;
        if (!parsed.connected) state.files = initialState.files;
      })
      .addCase(fetchDriveConnectionStatus.rejected, (state, action) => {
        state.status.loading = false;
        state.status.checked = true;
        state.status.error = action.payload;
        state.status.connected = false;
        state.files = initialState.files;
      })

      .addCase(authorizeDriveScope.pending, (state) => {
        state.authorize.loading = true;
        state.authorize.error = null;
      })
      .addCase(authorizeDriveScope.fulfilled, (state, action) => {
        state.authorize.loading = false;
        state.authorize.data = action.payload;
      })
      .addCase(authorizeDriveScope.rejected, (state, action) => {
        state.authorize.loading = false;
        state.authorize.error = action.payload;
      })

      .addCase(revokeDriveScope.pending, (state) => {
        state.revoke.loading = true;
        state.revoke.error = null;
      })
      .addCase(revokeDriveScope.fulfilled, (state, action) => {
        state.revoke.loading = false;
        state.revoke.data = action.payload;
        // The response carries the updated connection status.
        const parsed = parseDriveStatus(action.payload);
        state.status.connected = parsed.available ? parsed.connected : false;
        state.files = initialState.files;
      })
      .addCase(revokeDriveScope.rejected, (state, action) => {
        state.revoke.loading = false;
        state.revoke.error = action.payload;
      })

      .addCase(listDriveFiles.pending, (state) => {
        state.files.loading = true;
        state.files.error = null;
      })
      .addCase(listDriveFiles.fulfilled, (state, action) => {
        state.files.loading = false;
        state.files.data = toFileList(action.payload);
        state.files.nextPageToken = action.payload?.nextPageToken || null;
      })
      .addCase(listDriveFiles.rejected, (state, action) => {
        state.files.loading = false;
        state.files.error = action.payload;
      })

      .addCase(uploadDriveFile.pending, (state) => {
        state.upload.loading = true;
        state.upload.error = null;
      })
      .addCase(uploadDriveFile.fulfilled, (state, action) => {
        state.upload.loading = false;
        state.upload.data = action.payload;
        // Append the new file to the list straight away; the page also
        // refreshes the list so server-side ordering wins afterwards.
        const file = action.payload?.file || action.payload;
        if (file?.id && !state.files.data.some((f) => f?.id === file.id)) {
          state.files.data = [file, ...state.files.data];
        }
      })
      .addCase(uploadDriveFile.rejected, (state, action) => {
        state.upload.loading = false;
        state.upload.error = action.payload;
      })

      .addCase(downloadDriveFile.pending, (state, action) => {
        state.download.loading = true;
        state.download.error = null;
        state.download.fileId = action.meta.arg.fileId;
      })
      .addCase(downloadDriveFile.fulfilled, (state) => {
        state.download.loading = false;
        state.download.fileId = null;
      })
      .addCase(downloadDriveFile.rejected, (state, action) => {
        state.download.loading = false;
        state.download.error = action.payload;
      })

      .addCase(deleteDriveFile.pending, (state, action) => {
        state.remove.loading = true;
        state.remove.error = null;
        state.remove.fileId = action.meta.arg.fileId;
      })
      .addCase(deleteDriveFile.fulfilled, (state, action) => {
        state.remove.loading = false;
        state.remove.fileId = null;
        state.files.data = state.files.data.filter((f) => f?.id !== action.payload.fileId);
      })
      .addCase(deleteDriveFile.rejected, (state, action) => {
        state.remove.loading = false;
        state.remove.error = action.payload;
      });
  },
});

export const selectDriveConnected = (state) => !!state.googleDrive?.status?.connected;
// True when the backend exposes services.drive for this user (sandbox gate).
export const selectDriveAvailable = (state) => !!state.googleDrive?.status?.available;
export const selectDriveStatusChecked = (state) => !!state.googleDrive?.status?.checked;

export const { resetGoogleDriveState, clearDriveFiles } = googleDriveSlice.actions;
export default googleDriveSlice.reducer;
